import React, { useState, useEffect } from "react";
import { Col, Card, CardBody, Form, Input, Button } from "reactstrap";
import { Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchUninstallRequest, searchUninstallSurveyRequest } from "../../store/UnistallSurvey/uninstallAction";
import PageSection from '../../components/Loader/loader';

const UserUninstallSurvey = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(30);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [fetchedEntries, setFetchedEntries] = useState([]);
  const [totalFetchedCount, setTotalFetchedCount] = useState(0);
  const [noResultsFound, setNoResultsFound] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);

  const dispatch = useDispatch();
  const { userUninstallData, searchResults = [], loading } = useSelector((state) => state.userUninstall);

  // Fetch the initial uninstall data
  useEffect(() => {
    if (!userUninstallData || userUninstallData.length === 0) {
      dispatch(fetchUninstallRequest(1, 120));
    } else {
      setDisplayedItems(userUninstallData);
    }
  }, [dispatch, userUninstallData]);

  useEffect(() => {
    const handleSearch = () => {
      if (searchTerm.length >= 3) {
        setSearchLoading(true);
        dispatch(searchUninstallSurveyRequest(searchTerm));
      } else if (searchTerm.length === 0) {
        setDisplayedItems(userUninstallData);
        setNoResultsFound(false);
      }
    };

    const timeoutId = setTimeout(handleSearch, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, dispatch, userUninstallData]);

  useEffect(() => {
    if (searchTerm.length >= 3) {
      setSearchLoading(false);
      if (searchResults.length > 0) {
        setDisplayedItems(searchResults);
        setNoResultsFound(false);
      }
      else {
        setNoResultsFound(true);
        const clearItemsTimeout = setTimeout(() => {
          setDisplayedItems([]);
        }, 500);

        return () => clearTimeout(clearItemsTimeout);
      }
    }
    else {
      setDisplayedItems(userUninstallData);
      setNoResultsFound(false);
    }
  }, [searchResults, searchTerm, userUninstallData]);


  useEffect(() => {
    if (currentPage % 4 === 0 && totalFetchedCount <= currentPage * itemsPerPage) {
      const nextPage = currentPage / 4 + 1;
      dispatch(fetchUninstallRequest(nextPage, 120));
    }
  }, [currentPage, dispatch, totalFetchedCount]);

  // Pagination logic
  const totalPages = Math.ceil(displayedItems.length / itemsPerPage);
  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const displayedPaginationItems = displayedItems.slice(indexOfFirstItem, indexOfFirstItem + itemsPerPage);


  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const renderPageNumbers = () => {
    const pageNumbers = [];
    let startPage, endPage;

    if (totalPages <= 3) {
      startPage = 1;
      endPage = totalPages;
    } else {
      if (currentPage <= 2) {
        startPage = 1;
        endPage = 3;
      } else if (currentPage + 1 >= totalPages) {
        startPage = totalPages - 2;
        endPage = totalPages;
      } else {
        startPage = currentPage - 1;
        endPage = currentPage + 1;
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <li
          key={i}
          className={`page-item ${i === currentPage ? "active" : ""}`}
        >
          <Link to="#" className="page-link" onClick={() => paginate(i)}>
            {i}
          </Link>
        </li>
      );
    }

    return pageNumbers;
  };

  // CSV Download logic remains the same
  const downloadCSV = () => {
    const csvData = displayedItems.map((item, index) => ({
      No: index + 1,
      "Device ID": item.deviceId,
      "Device Name": item.deviceName,
      "Too Expensive": item.servey.tooExpensive
        ? `Yes ${item.servey.tooExpensiveText ? `(${item.servey.tooExpensiveText})` : ""}`
        : "No",
      "Don't Use Enough": item.servey.notEnough
        ? `Yes ${item.servey.notEnoughText ? `(${item.servey.notEnoughText})` : ""}`
        : "No",
      "Found Another App": item.servey.foundAnotherApp
        ? `Yes ${item.servey.foundAnotherAppText ? `(${item.servey.foundAnotherAppText})` : ""}`
        : "No",
      "Don't Like It": item.servey.dontLike
        ? `Yes ${item.servey.dontLikeText ? `(${item.servey.dontLikeText})` : ""}`
        : "No",
      "Need Feature": item.servey.needFeature
        ? `Yes ${item.servey.needFeatureText ? `(${item.servey.needFeatureText})` : ""}`
        : "No",
      Others: item.servey.other
        ? `Yes ${item.servey.otherText ? `(${item.servey.otherText})` : ""}`
        : "No",
    }));

    const csvHeader = Object.keys(csvData[0]).join(",");
    const csvRows = csvData.map((row) =>
      Object.values(row)
        .map((val) => `"${val}"`)
        .join(",")
    );
    const csvContent = [csvHeader, ...csvRows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "uninstall_survey.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <React.Fragment>
      <ToastContainer />
      <Col lg={12}>
        <Card style={{ paddingBottom: "55px" }}>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <div className="d-flex justify-content-between align-items-center">
                <h4 className="card-title mt-1">User Uninstall Survey</h4>
              </div>
              <div className="d-flex align-items-center">
                <Form className="app-search d-none d-lg-block pt-2" onSubmit={(e) => e.preventDefault()}>
                  <div className="position-relative d-flex align-items-center">
                    <Input
                      type="text"
                      className="form-control"
                      style={{ width: "65%" }}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search"
                    />
                    <span className="ri-search-line"></span>
                    <Button
                      style={{ fontSize: "13px", padding: "7px 9px" }}
                      color="primary"
                      className="ms-2"
                      onClick={downloadCSV}
                    >
                      Download CSV
                    </Button>
                  </div>
                </Form>
              </div>
            </div>
            {loading || searchLoading ? (
              <div className="table-responsive p-2" style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50%"
              }}>
                <PageSection />
              </div>
            ) : (
              <div className="table-responsive">
                <table
                  className="table table-bordered"
                  style={{ minWidth: "max-content" }}
                >
                  <thead>
                    <tr>
                      <th className="p-3">No.</th>
                      <th className="p-3">Device ID</th>
                      <th className="p-3">Device Name</th>
                      <th className="p-3">Too Expensive</th>
                      <th className="p-3">Don't Use Enough</th>
                      <th className="p-3">Found Another App</th>
                      <th className="p-3">Don't Like It</th>
                      <th className="p-3">Need Feature</th>
                      <th className="p-3">Others</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedPaginationItems.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="text-center p-5">
                          {noResultsFound ? (
                            <div>No data found</div>
                          ) : (
                            <PageSection />
                          )}
                        </td>
                      </tr>
                    ) : (
                      displayedPaginationItems.map((row, index) => (
                        <tr key={row.deviceId}>
                          <td style={{ maxWidth: "250px" }} className="p-3">{indexOfFirstItem + index + 1}</td>
                          <td style={{ maxWidth: "250px" }} className="p-3">{row.deviceId}</td>
                          <td style={{ maxWidth: "250px" }} className="p-3">{row.deviceName}</td>
                          <td style={{ maxWidth: "250px" }} className="p-3">{row.servey.tooExpensive ? `Yes ${row.servey.tooExpensiveText || ""}` : "No"}</td>
                          <td style={{ maxWidth: "250px" }} className="p-3">{row.servey.notEnough ? `Yes ${row.servey.notEnoughText || ""}` : "No"}</td>
                          <td style={{ maxWidth: "250px" }} className="p-3">{row.servey.foundAnotherApp ? `Yes ${row.servey.foundAnotherAppText || ""}` : "No"}</td>
                          <td style={{ maxWidth: "250px" }} className="p-3">{row.servey.dontLike ? `Yes ${row.servey.dontLikeText || ""}` : "No"}</td>
                          <td style={{ maxWidth: "250px" }} className="p-3">{row.servey.needFeature ? `Yes ${row.servey.needFeatureText || ""}` : "No"}</td>
                          <td style={{ maxWidth: "250px" }} className="p-3">{row.servey.other ? `Yes ${row.servey.otherText || ""}` : "No"}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {!loading && !noResultsFound && (
                  <nav>
                    <ul
                      className="pagination justify-content-end"
                      style={{
                        position: "absolute",
                        marginTop: "15px",
                        right: "20px",
                      }}
                    >
                      <li
                        className={`page-item ${currentPage === 1 ? "disabled" : ""
                          }`}
                      >
                        <Link
                          to="#"
                          className="page-link"
                          onClick={() => paginate(currentPage - 1)}
                        >
                          &laquo;
                        </Link>
                      </li>
                      {renderPageNumbers()}
                      <li
                        className={`page-item ${currentPage === totalPages ? "disabled" : ""
                          }`}
                      >
                        <Link
                          to="#"
                          className="page-link"
                          onClick={() => paginate(currentPage + 1)}
                        >
                          &raquo;
                        </Link>
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default UserUninstallSurvey;
